import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Plus, Trash2, Calculator, Settings, Copy, Pencil, AlertTriangle, CheckCircle, XCircle, TrendingUp, TrendingDown } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

const FORMATI = [
  { value: "percentuale", label: "Percentuale (%)" },
  { value: "valuta", label: "Valuta (€)" },
  { value: "numero", label: "Numero" },
];

const CAMPI_DISPONIBILI = [
  { value: "ricavi_vendita", label: "Ricavi Vendita (Fatture FV)", categoria: "fatture" },
  { value: "costi_acquisto", label: "Costi Acquisto (Fatture FA)", categoria: "fatture" },
  { value: "margine_lordo", label: "Margine Lordo (Ricavi - Costi)", categoria: "derivati" },
  { value: "entrate_cassa", label: "Entrate Cassa", categoria: "movimenti" },
  { value: "uscite_cassa", label: "Uscite Cassa", categoria: "movimenti" },
  { value: "saldo_banche", label: "Saldo Banche", categoria: "movimenti" },
  { value: "cash_flow_netto", label: "Cash Flow Netto", categoria: "derivati" },
  { value: "costi_personale", label: "Costi Personale", categoria: "costi_extra" },
  { value: "totale_attivo", label: "Totale Attivo", categoria: "bilancio" },
  { value: "totale_passivo", label: "Totale Passivo", categoria: "bilancio" },
  { value: "patrimonio_netto", label: "Patrimonio Netto", categoria: "bilancio" },
];

const TIPI_FORMULA = [
  { value: "rapporto", label: "Rapporto (A / B * 100)", desc: "Calcola il rapporto percentuale tra due valori" },
  { value: "differenza", label: "Differenza (A - B)", desc: "Calcola la differenza tra valori" },
  { value: "somma", label: "Somma (A + B + ...)", desc: "Somma di più valori" },
  { value: "custom", label: "Personalizzato", desc: "Formula personalizzata con più campi" },
];

export default function IndiciPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [indici, setIndici] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("miei");
  
  const [form, setForm] = useState({
    nome: "",
    descrizione: "",
    formato: "percentuale",
    soglia_warning: "",
    soglia_danger: "",
    tipo_formula: "rapporto",
    numeratore: "",
    denominatore: "",
    campi_selezionati: []
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [indiciRes, templatesRes] = await Promise.all([
        api.get(`${prefix}/budget/indices`),
        api.get(`${prefix}/budget/indices/templates`)
      ]);
      setIndici(indiciRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadIndices"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buildFormula = () => {
    const { tipo_formula, numeratore, denominatore, campi_selezionati } = form;
    
    if (tipo_formula === "rapporto") {
      return JSON.stringify({
        tipo: "rapporto",
        numeratore: { fonte: "aggregati", campo: numeratore },
        denominatore: { fonte: "aggregati", campo: denominatore }
      });
    } else if (tipo_formula === "differenza") {
      return JSON.stringify({
        tipo: "differenza",
        addendi: [
          { fonte: "aggregati", campo: numeratore, segno: "+" },
          { fonte: "aggregati", campo: denominatore, segno: "-" }
        ]
      });
    } else if (tipo_formula === "somma" || tipo_formula === "custom") {
      return JSON.stringify({
        tipo: "custom",
        campi: campi_selezionati,
        operazione: tipo_formula === "somma" ? "somma" : "rapporto"
      });
    }
    
    return "{}";
  };

  const handleSave = async () => {
    if (!form.nome) {
      handleApiError({ message: t("errors.requiredIndexName") });
      return;
    }
    
    try {
      const prefix = getApiPrefix();
      const payload = {
        nome: form.nome,
        descrizione: form.descrizione,
        formula: buildFormula(),
        formato: form.formato,
        soglia_warning: form.soglia_warning ? parseFloat(form.soglia_warning) : null,
        soglia_danger: form.soglia_danger ? parseFloat(form.soglia_danger) : null
      };
      
      if (editingId) {
        await api.put(`${prefix}/budget/indices/${editingId}`, payload);
        showSuccess(t("success.indexUpdated"));
      } else {
        await api.post(`${prefix}/budget/indices`, payload);
        showSuccess(t("success.indexCreated"));
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteIndex"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/budget/indices/${id}`);
      showSuccess(t("success.indexDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleCopyTemplate = async (template) => {
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/budget/indices`, {
        nome: template.nome,
        descrizione: template.descrizione,
        formula: template.formula,
        formato: template.formato,
        soglia_warning: null,
        soglia_danger: null
      });
      showSuccess(`Indice "${template.nome}" aggiunto`);
      fetchData();
      setActiveTab("miei");
    } catch (err) {
      handleApiError(err, t("errors.errorCopyTemplate"));
    }
  };

  const resetForm = () => {
    setForm({
      nome: "",
      descrizione: "",
      formato: "percentuale",
      soglia_warning: "",
      soglia_danger: "",
      tipo_formula: "rapporto",
      numeratore: "",
      denominatore: "",
      campi_selezionati: []
    });
    setEditingId(null);
  };

  const openNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (indice) => {
    setEditingId(indice.id);
    let tipoFormula = "rapporto";
    let numeratore = "";
    let denominatore = "";
    let campiSelezionati = [];
    
    try {
      const formula = JSON.parse(indice.formula);
      tipoFormula = formula.tipo || "rapporto";
      if (formula.numeratore?.campo) numeratore = formula.numeratore.campo;
      if (formula.denominatore?.campo) denominatore = formula.denominatore.campo;
      if (formula.campi) campiSelezionati = formula.campi;
    } catch (e) {}
    
    setForm({
      nome: indice.nome,
      descrizione: indice.descrizione || "",
      formato: indice.formato || "percentuale",
      soglia_warning: indice.soglia_warning || "",
      soglia_danger: indice.soglia_danger || "",
      tipo_formula: tipoFormula,
      numeratore: numeratore,
      denominatore: denominatore,
      campi_selezionati: campiSelezionati
    });
    setDialogOpen(true);
  };

  const groupTemplatesByCategory = () => {
    const groups = {};
    templates.forEach(t => {
      if (!groups[t.categoria]) groups[t.categoria] = [];
      groups[t.categoria].push(t);
    });
    return groups;
  };

  if (loading) return <PageLoader />;

  const templateGroups = groupTemplatesByCategory();

  return (
    <div className="p-6 space-y-6" data-testid="indici-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t("budget.indices")}</h1>
          <p className="text-muted-foreground">Crea e gestisci indici personalizzati per monitorare le performance</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Indice
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="miei">I Miei Indici ({indici.length})</TabsTrigger>
          <TabsTrigger value="templates">Template Predefiniti</TabsTrigger>
        </TabsList>

        <TabsContent value="miei" className="mt-4">
          {indici.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Nessun indice configurato</h3>
                <p className="text-muted-foreground mb-4">
                  Crea i tuoi indici personalizzati o copia dai template predefiniti
                </p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={openNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crea Nuovo
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab("templates")}>
                    <Copy className="h-4 w-4 mr-2" />
                    Sfoglia Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {indici.map(indice => (
                <Card key={indice.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{indice.nome}</CardTitle>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(indice)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(indice.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    {indice.descrizione && (
                      <CardDescription>{indice.descrizione}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{FORMATI.find(f => f.value === indice.formato)?.label || indice.formato}</Badge>
                      </div>
                      {(indice.soglia_warning || indice.soglia_danger) && (
                        <div className="text-sm text-muted-foreground">
                          {indice.soglia_warning && (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 text-yellow-500" />
                              Warning: {indice.soglia_warning}
                            </span>
                          )}
                          {indice.soglia_danger && (
                            <span className="flex items-center gap-1">
                              <XCircle className="h-3 w-3 text-red-500" />
                              Danger: {indice.soglia_danger}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4 space-y-6">
          {Object.entries(templateGroups).map(([categoria, items]) => (
            <Card key={categoria}>
              <CardHeader>
                <CardTitle className="text-lg capitalize">{categoria.replace('_', ' ')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.name")}</TableHead>
                      <TableHead>{t("common.description")}</TableHead>
                      <TableHead>Formato</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(template => (
                      <TableRow key={template.id}>
                        <TableCell className="font-medium">{template.nome}</TableCell>
                        <TableCell className="text-muted-foreground">{template.descrizione}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{template.formato}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => handleCopyTemplate(template)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Usa
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo/Modifica Indice */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? t("indices.editIndex") : t("indices.newIndex")}</DialogTitle>
            <DialogDescription>
              Configura un indice di bilancio scegliendo i valori da includere nel calcolo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Indice *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Es: ROE, Margine Netto..."
                />
              </div>
              <div className="space-y-2">
                <Label>Formato Risultato</Label>
                <Select value={form.formato} onValueChange={(v) => setForm({ ...form, formato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FORMATI.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                placeholder="Descrizione dell'indice e come interpretarlo..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo Formula</Label>
              <Select value={form.tipo_formula} onValueChange={(v) => setForm({ ...form, tipo_formula: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPI_FORMULA.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div>
                        <p className="font-medium">{t.label}</p>
                        <p className="text-xs text-muted-foreground">{t.desc}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(form.tipo_formula === "rapporto" || form.tipo_formula === "differenza") && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{form.tipo_formula === "rapporto" ? "Numeratore" : "Valore A (+)"}</Label>
                  <Select value={form.numeratore} onValueChange={(v) => setForm({ ...form, numeratore: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleziona campo..." /></SelectTrigger>
                    <SelectContent>
                      {CAMPI_DISPONIBILI.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{form.tipo_formula === "rapporto" ? "Denominatore" : "Valore B (-)"}</Label>
                  <Select value={form.denominatore} onValueChange={(v) => setForm({ ...form, denominatore: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleziona campo..." /></SelectTrigger>
                    <SelectContent>
                      {CAMPI_DISPONIBILI.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {(form.tipo_formula === "somma" || form.tipo_formula === "custom") && (
              <div className="space-y-2">
                <Label>Seleziona Campi da Sommare</Label>
                <div className="grid grid-cols-2 gap-2 p-3 border rounded-md max-h-40 overflow-y-auto">
                  {CAMPI_DISPONIBILI.map(c => (
                    <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.campi_selezionati.includes(c.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm({ ...form, campi_selezionati: [...form.campi_selezionati, c.value] });
                          } else {
                            setForm({ ...form, campi_selezionati: form.campi_selezionati.filter(x => x !== c.value) });
                          }
                        }}
                        className="rounded"
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Soglia Warning (opzionale)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.soglia_warning}
                  onChange={(e) => setForm({ ...form, soglia_warning: e.target.value })}
                  placeholder="Es: 50 per 50%"
                />
                <p className="text-xs text-muted-foreground">Alert giallo se il valore supera questa soglia</p>
              </div>
              <div className="space-y-2">
                <Label>Soglia Danger (opzionale)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.soglia_danger}
                  onChange={(e) => setForm({ ...form, soglia_danger: e.target.value })}
                  placeholder="Es: 80 per 80%"
                />
                <p className="text-xs text-muted-foreground">Alert rosso se il valore supera questa soglia</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave}>
              {editingId ? t("common.save") : "Crea Indice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
